<template>
  <VueFinalModal
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :click-to-close="!submitting"
    :esc-to-close="!submitting"
    classes="documents-modal"
    :content-class="contentClass"
  >
    <template #default>
      <button
        class="modal__close icons-button"
        @click="close"
        title="Chiudi"
        type="button"
        :disabled="submitting"
      >
        <i class="fa-solid fa-xmark"></i>
      </button>

      <h3 class="modal__title">Crea nuovo articolo</h3>

      <!-- ── STEP 1: categoria ──────────────────────────────────────────── -->
      <template v-if="step === 'category'">
        <p class="modal__subtitle">Scegli la categoria del nuovo articolo.</p>
        <div class="cim-step-row">
          <div class="field cim-step-row__select">
            <label>Categoria *</label>
            <StatusSelect
              v-model="category"
              :options="categoryOptions"
              placeholder="Seleziona categoria..."
              placeholderIcon="fa-tag"
            />
          </div>
          <div class="modal-actions cim-step-row__actions">
            <button class="secondary-btn" type="button" @click="close">
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              type="button"
              :disabled="!category"
              @click="goFromCategory"
            >
              Continua
            </button>
          </div>
        </div>
      </template>

      <!-- ── STEP 1b: sotto-categoria (solo dove richiesta, es. piastrelle) ─ -->
      <template v-else-if="step === 'subcategory'">
        <p class="modal__subtitle">
          Scegli la tipologia per «{{ categoryLabel }}».
        </p>
        <div class="cim-step-row">
          <div class="field cim-step-row__select">
            <label>Tipologia *</label>
            <StatusSelect
              v-model="form.subCategory"
              :options="subCategoryOptions"
              placeholder="Seleziona tipologia..."
              placeholderIcon="fa-tag"
            />
          </div>
          <div class="modal-actions cim-step-row__actions">
            <button class="secondary-btn" type="button" @click="backToCategory">
              Indietro
            </button>
            <button
              class="primary-btn confirm-btn"
              type="button"
              :disabled="!form.subCategory"
              @click="goFromSubCategory"
            >
              Continua
            </button>
          </div>
        </div>
      </template>

      <!-- ── STEP 2: form dinamico ──────────────────────────────────────── -->
      <form
        v-else
        class="create-item-form create-item-form-grid"
        @submit.prevent="onSubmit"
      >
        <!-- Dati principali -->
        <div class="field field--span-6">
          <label for="cim-item-code">Codice articolo</label>
          <input
            id="cim-item-code"
            type="text"
            v-model.trim="form.itemCode"
            placeholder="es. ART-001"
            maxlength="255"
          />
          <small style="color: #718096; font-size: 0.75rem"
            >Facoltativo: può essere lasciato vuoto.</small
          >
        </div>
        <div class="field field--span-6">
          <label for="cim-item-name">Nome articolo *</label>
          <input
            id="cim-item-name"
            type="text"
            v-model.trim="form.itemName"
            placeholder="inserisci"
            required
          />
        </div>

        <div class="field" :class="mainColClass">
          <label for="cim-brand">Brand</label>
          <input
            id="cim-brand"
            type="text"
            v-model="form.brand"
            placeholder="inserisci"
          />
        </div>
        <div class="field" :class="mainColClass">
          <label for="cim-serie">Serie</label>
          <input
            id="cim-serie"
            type="text"
            v-model="form.serie"
            placeholder="inserisci"
          />
        </div>
        <!-- Prezzo comune: nascosto per piastrelle (il prezzo/unità del
             blocco piastrelle scrive comunque in form.price, vedi sotto). -->
        <div class="field field--span-4" v-if="category !== 'piastrelle'">
          <label for="cim-price">{{ priceLabel }} — IVA esclusa</label>
          <input
            id="cim-price"
            type="number"
            step="any"
            min="0"
            v-model.number="form.price"
            placeholder="inserisci"
          />
        </div>
        <div class="field field--span-6">
          <label for="cim-sales-unit">Unità di vendita *</label>
          <StatusSelect
            v-model="form.salesUnit"
            :options="salesUnitOptions"
            placeholder="Seleziona unità..."
            placeholderIcon="fa-ruler-combined"
          />
        </div>
        <div class="field field--span-6">
          <label for="cim-status">Stato</label>
          <StatusSelect
            v-model="form.articleStatus"
            :options="articleStatusOptions"
          />
        </div>

        <hr />

        <!-- [sc-48351] I campi della categoria, gli stessi della creazione da
             Listini: prima qui ce n'era solo una parte. La tipologia delle
             piastrelle si è già scelta nel passo precedente. -->
        <ItemCategoryFields
          :form="form"
          :category="category"
          :chiedi-tipologia="false"
        />

        <hr />

        <div class="modal-actions">
          <button
            class="secondary-btn"
            type="button"
            :disabled="submitting"
            @click="backFromForm"
          >
            Indietro
          </button>
          <button
            class="primary-btn confirm-btn"
            type="submit"
            :disabled="isReadOnly || !canSave || submitting"
            :title="!canSave ? 'Inserisci il nome articolo' : 'Salva articolo'"
          >
            <span v-if="submitting">Salvataggio…</span>
            <span v-else>Salva</span>
          </button>
        </div>
      </form>
    </template>
  </VueFinalModal>
</template>

<script>
import { mapActions } from "vuex/dist/vuex.cjs.js";
import { VueFinalModal } from "vue-final-modal";
import StatusSelect from "@/components/StatusSelect.vue";
import ItemCategoryFields from "@/components/ItemCategoryFields.vue";
import { salesUnitsFor } from "@/config/catalog";
import {
  TILE_SUBCATEGORIES,
  creationCategoriesFor,
  etichettaCategoria,
  priceLabelFor,
} from "@/config/itemFields";

function emptyForm() {
  return {
    itemCode: "",
    itemName: "",
    brand: "",
    serie: "",
    price: 0,
    salesUnit: "",
    articleStatus: "attivo",
    subCategory: "",
    formatLabel: "",
    thicknessMm: null,
    surface: "",
    color: "",
    piecesPerBox: null,
    sqmPerBox: null,
    fixtureType: "",
    fixturePosition: "",
    finish: "",
    material: "",
    lengthMm: null,
    mlPerBox: null,
    widthCm: null,
    heightCm: null,
    specs: { scelta: "" },
    notes: "",
  };
}

export default {
  name: "CreateItemModal",
  components: { VueFinalModal, StatusSelect, ItemCategoryFields },
  props: {
    modelValue: { type: Boolean, default: false },
    // Testo cercato nel campo articolo del preventivo: precompila itemName
    // all'apertura. Opzionale.
    initialName: { type: String, default: "" },
  },
  emits: ["update:modelValue", "created"],
  data() {
    return {
      step: "category", // 'category' | 'form'
      submitting: false,
      category: "",
      articleStatusOptions: [
        { value: "attivo", label: "Attivo", color: "#059669" },
        { value: "novita", label: "Novità", color: "#2563eb" },
        { value: "discontinuo", label: "Discontinuo", color: "#9ca3af" },
      ],
      subCategoryOptions: TILE_SUBCATEGORIES.map((voce) => ({
        value: voce,
        label: voce,
      })),
      form: emptyForm(),
    };
  },
  computed: {
    salesUnitOptions() {
      // Le unità scelte in configurazione iniziale; nessuna scelta significa
      // tutte, così una istanza mai configurata resta usabile.
      return salesUnitsFor(this.$store.state.companyProfileUnits);
    },
    defaultSalesUnit() {
      return this.salesUnitOptions.length === 1
        ? this.salesUnitOptions[0].value
        : "";
    },
    priceLabel() {
      return priceLabelFor(this.form.salesUnit);
    },
    /**
     * [sc-48351] Le categorie delle schede scelte in configurazione, come
     * nella creazione da Listini. Prima erano sei, scritte a mano: mancavano
     * sanitari, rubinetteria, mobili... e ne proponeva di non ammesse.
     */
    categoryOptions() {
      const branding = this.$store.getters["company/branding"];
      return creationCategoriesFor(branding?.productCategories).map((voce) => ({
        value: voce,
        label: etichettaCategoria(voce),
      }));
    },
    // Categorie che richiedono la scelta di una sotto-categoria prima del form
    // (come nel flusso Listini: piastrelle). "bagno" è escluso dalla modale.
    requiresSubCategory() {
      return this.category === "piastrelle";
    },
    categoryLabel() {
      const opt = this.categoryOptions.find((c) => c.value === this.category);
      return opt ? opt.label : "";
    },
    // Classe del box modale, con modificatore per-step: gli step compatti
    // restano stretti, lo step "form" si allarga (vedi <style> non-scoped).
    contentClass() {
      return `confirm-modal-content create-item-modal create-item-modal--${this.step}`;
    },
    // Span dei campi Brand/Serie nella griglia: un terzo di riga quando c'è
    // anche il Prezzo (categorie non-piastrelle → 3 campi in riga), metà riga
    // altrimenti. Unità di vendita e Stato stanno nella riga successiva, a
    // metà ciascuno: così in tutti e due i casi le righe restano piene.
    mainColClass() {
      return this.category === "piastrelle" ? "field--span-6" : "field--span-4";
    },
    canSave() {
      if (!(this.form.itemName || "").trim()) return false;
      if (!this.form.salesUnit) return false;
      if (this.category === "piastrelle" && !this.form.subCategory)
        return false;
      return true;
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        // Precompila itemName dal testo cercato, senza sovrascrivere input utente.
        if (this.initialName && !this.form.itemName) {
          this.form.itemName = this.initialName;
        }
        // Con una sola unità non c'è niente da scegliere: si preseleziona, così
        // il campo obbligatorio non diventa un ostacolo inutile.
        if (!this.form.salesUnit) this.form.salesUnit = this.defaultSalesUnit;
      }
    },
    category(next, prev) {
      // Cambiando categoria (fuori da piastrelle) azzera la sotto-categoria.
      if (prev === "piastrelle" && next !== "piastrelle") {
        this.form.subCategory = "";
      }
    },
  },
  methods: {
    ...mapActions(["createItem"]),

    reset() {
      this.step = "category";
      this.category = "";
      this.submitting = false;
      this.form = emptyForm();
    },
    close() {
      if (this.submitting) return;
      this.reset();
      this.$emit("update:modelValue", false);
    },
    // Step categoria → sotto-categoria (se richiesta) oppure direttamente form.
    goFromCategory() {
      if (!this.category) return;
      if (this.requiresSubCategory) {
        this.step = "subcategory";
        return;
      }
      this.enterForm();
    },
    // Step sotto-categoria → form (gate: sotto-categoria obbligatoria).
    goFromSubCategory() {
      if (this.requiresSubCategory && !this.form.subCategory) return;
      this.enterForm();
    },
    enterForm() {
      // Precompila itemName all'ingresso nel form se non ancora valorizzato.
      if (this.initialName && !this.form.itemName) {
        this.form.itemName = this.initialName;
      }
      this.step = "form";
    },
    backToCategory() {
      // Torna allo step categoria senza perdere i dati inseriti.
      this.step = "category";
    },
    backFromForm() {
      // Dal form torna alla sotto-categoria se prevista, altrimenti alla categoria.
      this.step = this.requiresSubCategory ? "subcategory" : "category";
    },

    async onSubmit() {
      if (!this.canSave || this.submitting) return;
      this.submitting = true;

      // Payload costruito identico a CreateItemPage.onSubmit (stessa
      // normalizzazione di specs), così il backend riceve la stessa forma.
      const payload = {
        ...this.form,
        category: this.category,
      };
      // Come in Listini: la giacenza si carica dal magazzino, non da qui.
      delete payload.boxQuantity;
      if (this.category === "parquet") {
        payload.specs = this.form.specs?.scelta
          ? { scelta: this.form.specs.scelta }
          : null;
      } else {
        delete payload.specs;
      }

      try {
        const res = await this.createItem(payload);
        this.$emit("created", {
          item: res?.data?.item ?? null,
          itemCode: payload.itemCode,
          message: res?.data?.message || "Articolo creato",
        });
        this.reset();
        this.$emit("update:modelValue", false);
      } catch (err) {
        this.handleError(err);
      } finally {
        this.submitting = false;
      }
    },

    handleError(err) {
      if (
        err.response?.status === 409 &&
        err.response?.data?.code === "ITEM_CODE_DUPLICATE"
      ) {
        this.$toast.error(
          "Questo codice è già utilizzato da un altro articolo."
        );
      } else {
        this.$toast.error(
          err.response?.data?.message ||
            err.response?.data?.error ||
            "Errore durante la creazione."
        );
      }
    },
  },
};
</script>

<style scoped>
/* === Step compatti (categoria / sotto-categoria) =========================
   Una sola riga: select a sinistra, gruppo pulsanti a destra. */
.cim-step-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin: 14px 0 2px;
}
.cim-step-row__select {
  flex: 1 1 260px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
/* La select riempie la parte sinistra della riga */
.cim-step-row__select :deep(.status-select),
.cim-step-row__select :deep(.status-select__trigger) {
  width: 100%;
}
/* Neutralizza il margin-top globale di .modal-actions dentro la riga */
.modal-actions.cim-step-row__actions {
  margin: 0;
  flex: 0 0 auto;
}
@media (max-width: 560px) {
  .cim-step-row {
    flex-direction: column;
    align-items: stretch;
  }
  .cim-step-row__actions {
    justify-content: flex-end;
  }
}

/* === Step form: griglia solida a 12 colonne, scroll verticale interno ===== */
.create-item-form-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 18px 24px;
  align-content: start;
  width: 100%;
  min-width: 0;
  margin: 14px 0 4px;
  max-height: calc(90vh - 150px);
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}
/* Campi = celle della griglia: mai più larghi della colonna assegnata */
.create-item-form-grid .field {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.field--span-3 {
  grid-column: span 3;
}
.field--span-4 {
  grid-column: span 4;
}
.field--span-6 {
  grid-column: span 6;
}
.field--span-12 {
  grid-column: span 12;
}
/* Separatore e barra azioni occupano l'intera riga */
.create-item-form-grid hr {
  grid-column: 1 / -1;
  width: 100%;
  border: none;
  border-top: 0.5px solid var(--border, #e5e7eb);
  margin: 2px 0;
}
.create-item-form-grid .modal-actions {
  grid-column: 1 / -1;
}
/* [sc-48351] Le righe dei campi di categoria vengono dal componente comune:
   qui occupano tutta la larghezza e si dispongono a griglia. */
.create-item-form-grid :deep(.create-item-row) {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 18px 24px;
  align-items: end;
}
.create-item-form-grid :deep(.create-item-row .field) {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.create-item-form-grid :deep(.create-item-row .field input),
.create-item-form-grid :deep(.create-item-row .field textarea),
.create-item-form-grid :deep(.create-item-row .status-select),
.create-item-form-grid :deep(.create-item-row .status-select__trigger) {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}
/* Controlli sempre a larghezza cella, niente overflow orizzontale */
.create-item-form-grid .field input,
.create-item-form-grid .field select,
.create-item-form-grid .field textarea {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}
.create-item-form-grid .field :deep(.status-select),
.create-item-form-grid .field :deep(.status-select__trigger) {
  width: 100%;
  min-width: 0;
}
/* Mobile: colonna singola, ogni campo full-width */
@media (max-width: 768px) {
  .create-item-form-grid {
    grid-template-columns: 1fr;
  }
  .create-item-form-grid .field,
  .create-item-form-grid [class*="field--span"] {
    grid-column: 1 / -1;
    width: 100%;
  }
}
</style>

<!-- Dimensioni del box modale: NON-scoped perché il content-box di
     VueFinalModal è teleportato (senza data-v). Namespacizzato su
     .create-item-modal → nessun impatto sugli altri modali. -->
<style>
.documents-modal .confirm-modal-content.create-item-modal {
  width: min(92vw, 560px);
  max-width: 92vw;
  max-height: min(85vh, 620px);
}
/* Step form: molto più ampio e alto, come una mini-pagina desktop */
.documents-modal .confirm-modal-content.create-item-modal--form {
  width: min(1460px, 95vw);
  max-width: 98vw;
  max-height: 90vh;
}
/* Mobile: margini laterali 2.5% → larghezza 95vw, nessun overflow orizzontale */
@media (max-width: 768px) {
  .documents-modal .confirm-modal-content.create-item-modal,
  .documents-modal .confirm-modal-content.create-item-modal--form {
    width: 95vw;
    max-width: 95vw;
    margin: 0;
  }
}
</style>
